/**
 * FleetOps API Client
 * Save as: src/api.js
 *
 * All functions return data shaped to match the existing
 * frontend state structures (same field names as the original mock data).
 */

const BASE = process.env.REACT_APP_ASSET_SENTINEL_API_URL || "http://localhost:3001/api";

// Set after login — included as X-Agency-ID and X-User-ID headers on every request
let _sessionAgencyID = 0;
let _sessionUserID   = 0;
export function setSessionAgencyID(id) {
  _sessionAgencyID = parseInt(id) || 0;
}
export function setSessionUserID(id) {
  _sessionUserID = parseInt(id) || 0;
}

async function request(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Agency-ID": _sessionAgencyID,
      "X-User-ID":   _sessionUserID,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── CODES ──────────────────────────────────────────────────────
export const fetchCodes         = ()            => request("GET",   "/codes");
export const updateCode         = (table, id, data) => request("PATCH", `/codes/${table}/${id}`, data);
export const createCode         = (table, data) => request("POST",  `/codes/${table}`, data);

// ── EMPLOYEES ──────────────────────────────────────────────────
export const fetchEmployees     = ()            => request("GET",   "/employees");
export const createEmployee     = (data)        => request("POST",  "/employees", data);
export const updateEmployee     = (id, data)    => request("PATCH", `/employees/${id}`, data);

// ── VEHICLES ───────────────────────────────────────────────────
export const fetchVehicles      = ()            => request("GET",   "/vehicles");
export const createVehicle      = (data)        => request("POST",  "/vehicles", data);
export const patchVehicleStatus = (id, status) => request("PATCH", `/vehicles/${id}/status`, { status });
export const updateVehicle      = (id, data)    => request("PATCH", `/vehicles/${id}`, data);
export const deleteVehicle      = (id)          => request("DELETE",`/vehicles/${id}`);

// ── VEHICLE NOTES ──────────────────────────────────────────────
export const fetchVehicleNotes  = (vehicleID)   => request("GET",   `/vehicle-notes?vehicleID=${vehicleID}`);
export const createVehicleNote  = (data)        => request("POST",  "/vehicle-notes", data);
export const updateVehicleNote  = (id, data)    => request("PATCH", `/vehicle-notes/${id}`, data);
export const deleteVehicleNote  = (id)          => request("DELETE",`/vehicle-notes/${id}`);

// ── VEHICLE ASSIGNMENT HISTORY ─────────────────────────────────
export const fetchVehicleAssignmentHistory = (vehicleID) =>
  request("GET", vehicleID ? `/vehicle-assignment-history?vehicleID=${vehicleID}` : "/vehicle-assignment-history");
export const createVehicleAssignmentHistory = (data)     => request("POST",  "/vehicle-assignment-history", data);
export const closeVehicleAssignmentHistory  = (id, data) => request("PATCH", `/vehicle-assignment-history/${id}/close`, data);

// ── WORK ORDERS ────────────────────────────────────────────────
export const fetchWorkOrders    = ()            => request("GET",   "/work-orders");
export const createWorkOrder    = (data)        => request("POST",  "/work-orders", data);
export const updateWorkOrder    = (id, data)    => request("PATCH", `/work-orders/${id}`, data);
export const deleteWorkOrder    = (id)          => request("DELETE",`/work-orders/${id}`);

// ── EQUIPMENT ──────────────────────────────────────────────────
export const fetchEquipment     = ()            => request("GET",   "/equipment");
export const createEquipment    = (data)        => request("POST",  "/equipment", data);
export const updateEquipment    = (id, data)    => request("PATCH", `/equipment/${id}`, data);
export const deleteEquipment    = (id)          => request("DELETE",`/equipment/${id}`);
export const fetchEqSvcReqs     = ()            => request("GET",   "/equipment-svc-reqs");
export const createEqSvcReq     = (data)        => request("POST",  "/equipment-svc-reqs", data);
export const updateEqSvcReq     = (id, data)    => request("PATCH", `/equipment-svc-reqs/${parseInt(String(id).replace("eq",""))}`, data);
export const deleteEqSvcReq     = (id)          => request("DELETE",`/equipment-svc-reqs/${parseInt(String(id).replace("eq",""))}`);

// ── EQUIPMENT ASSIGNMENT HISTORY ───────────────────────────────
export const fetchEquipmentAssignmentHistory = (equipmentID) =>
  request("GET", equipmentID ? `/equipment-assignment-history?equipmentID=${equipmentID}` : "/equipment-assignment-history");
export const createEquipmentAssignmentHistory = (data) => request("POST", "/equipment-assignment-history", data);

// ── INSPECTIONS ────────────────────────────────────────────────
export const fetchInspections   = ()            => request("GET",   "/inspections");
export const createInspection   = (data)        => request("POST",  "/inspections", data);
export const updateInspection   = (id, data)    => request("PATCH", `/inspections/${id}`, data);

// ── CHECKLIST ITEMS ────────────────────────────────────────────
export const fetchChecklistItems  = ()          => request("GET",   "/checklist-items");
export const createChecklistItem  = (data)      => request("POST",  "/checklist-items", data);
export const updateChecklistItem  = (id, data)  => request("PATCH", `/checklist-items/${id}`, data);

// ── FUNDING SOURCES ────────────────────────────────────────────
export const fetchFundingSources  = ()          => request("GET",   "/funding-sources");
export const createFundingSource  = (data)      => request("POST",  "/funding-sources", data);
export const updateFundingSource  = (id, data)  => request("PATCH", `/funding-sources/${id}`, data);

// ── MAINTENANCE ────────────────────────────────────────────────
export const fetchMaintenance     = ()          => request("GET",   "/maintenance");
export const createMaintenance    = (data)      => request("POST",  "/maintenance", data);
export const updateMaintenance    = (id, data)  => request("PATCH", `/maintenance/${id}`, data);
export const deleteMaintenance    = (id)        => request("DELETE",`/maintenance/${id}`);

// ── AGENCIES ──────────────────────────────────────────────────
export const fetchAgencies      = ()           => request("GET",    "/agencies");
export const fetchAgency        = (id)         => request("GET",    `/agencies/${id}`);
export const createAgency       = (data)       => request("POST",   "/agencies", data);
export const updateAgency       = (id, data)   => request("PATCH",  `/agencies/${id}`, data);
export const deleteAgency       = (id)         => request("DELETE", `/agencies/${id}`);

// ── USER AGENCIES ──────────────────────────────────────────────
export const fetchUserAgencies       = (userID) => request("GET",    `/user-agencies?userID=${userID}`);
export const fetchUserAgenciesByEmail = (email)  => request("GET",    `/user-agencies/by-email?email=${encodeURIComponent(email)}`);
export const addUserAgency      = (data)       => request("POST",   "/user-agencies", data);
export const updateUserAgency   = (id, data)   => request("PATCH",  `/user-agencies/${id}`, data);
export const deleteUserAgency   = (id)         => request("DELETE", `/user-agencies/${id}`);
export const selectAgency       = (data)       => request("POST",   "/rbac/select-agency", data);

// ── SETTINGS ──────────────────────────────────────────────────
export const fetchSettings        = ()          => request("GET",   "/settings");
export const updateSettings       = (data)      => request("PATCH", "/settings", data);

// ── AUDIT LOG ──────────────────────────────────────────────────
export const fetchAuditLog        = ()          => request("GET",   "/audit-log");

// ── RBAC ───────────────────────────────────────────────────────
export const fetchRbacPermissions = ()              => request("GET",    "/rbac/permissions");
export const fetchRbacRoles       = ()              => request("GET",    "/rbac/roles");
export const createRbacRole       = (data)          => request("POST",   "/rbac/roles", data);
export const updateRbacRole       = (id, data)      => request("PUT",    `/rbac/roles/${id}`, data);
export const sortRbacRole         = (id, sortOrder) => request("PATCH",  `/rbac/roles/${id}/sort`, { sortOrder });
export const deleteRbacRole       = (id)            => request("DELETE", `/rbac/roles/${id}`);

export const fetchRbacProfiles    = ()              => request("GET",    "/rbac/profiles");
export const createRbacProfile    = (data)          => request("POST",   "/rbac/profiles", data);
export const updateRbacProfile    = (id, data)      => request("PUT",    `/rbac/profiles/${id}`, data);
export const sortRbacProfile      = (id, sortOrder) => request("PATCH",  `/rbac/profiles/${id}/sort`, { sortOrder });
export const deleteRbacProfile    = (id)            => request("DELETE", `/rbac/profiles/${id}`);

export const fetchRbacUsers       = ()              => request("GET",    "/rbac/users");
export const createRbacUser       = (data)          => request("POST",   "/rbac/users", data);
export const updateRbacUser       = (id, data)      => request("PUT",    `/rbac/users/${id}`, data);
export const deleteRbacUser       = (id)            => request("DELETE", `/rbac/users/${id}`);

export const rbacLogin            = (username)      => request("POST",   "/rbac/login", { username });
export const fetchUserPerms       = (id)            => request("GET",    `/rbac/users/${id}/perms`);

export const deleteInspection                = (id)          => request("DELETE", `/inspections/${id}`);
export const addInspectionPhoto              = (id, data)    => request("POST",   `/inspections/${id}/photos`, data);
export const deleteInspectionPhoto           = (id, photoId) => request("DELETE", `/inspections/${id}/photos/${photoId}`);
export const updateEquipmentAssignmentHistory = (id, data)   => request("PATCH",  `/equipment-assignment-history/${id}`, data);

// ── DISPENSABLE ITEMS ──────────────────────────────────────────
export const fetchDispensableItems     = ()               => request("GET",    "/dispensable-items");
export const createDispensableItem     = (data)           => request("POST",   "/dispensable-items", data);
export const updateDispensableItem     = (id, data)       => request("PATCH",  `/dispensable-items/${id}`, data);
export const restockDispensableItem    = (id, data)       => request("PATCH",  `/dispensable-items/${id}/restock`, data);
export const deleteDispensableItem     = (id)             => request("DELETE", `/dispensable-items/${id}`);
export const createDispensableIssuance = (itemId, data)   => request("POST",   `/dispensable-items/${itemId}/issuances`, data);
